import React, { useEffect, useState, useCallback } from 'react';
import { sanitizePatternTemplate } from '../../utils/provisioningPreview';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../../utils/api';

const PLACEHOLDER_HELP = `Placeholders: {firstname}, {lastname} (full slug), {firstname_initial}, {lastname_initial} (first letter), {firstname_2}, {firstname_3}, {lastname_2}, {lastname_3} (first 2 or 3 letters, A–Z only), {domain}, {companydomain}, {domainname}. Optional: {prefix}, {suffix}, {middle} — set Prefix / Suffix / Middle fields below (API may return camelCase; the app reads both).

On save, {dev} in a template is normalized to {suffix}. Empty {middle} between dots is collapsed to one dot.

Between placeholders use only letters, digits, @, ., -, _. No quotation marks in the template.`;

/** Jane Doe @ example.com — for quick customer reference */
const PRESET_TEMPLATES = [
  { label: 'First dot last', template: '{firstname}.{lastname}@{domain}', example: 'jane.doe@example.com' },
  { label: 'Last dot first', template: '{lastname}.{firstname}@{domain}', example: 'doe.jane@example.com' },
  { label: 'First initial dot last', template: '{firstname_initial}.{lastname}@{domainname}', example: 'j.doe@example.com' },
  { label: 'First dot last initial', template: '{firstname}.{lastname_initial}@{domainname}', example: 'jane.d@example.com' },
  { label: 'First 2 letters dot last', template: '{firstname_2}.{lastname}@{domain}', example: 'ja.doe@example.com' },
  { label: 'First dot last 2 letters', template: '{firstname}.{lastname_2}@{domain}', example: 'jane.do@example.com' },
  { label: 'First 3 letters dot last', template: '{firstname_3}.{lastname}@{domain}', example: 'jan.doe@example.com' },
  { label: 'First dot last 3 letters', template: '{firstname}.{lastname_3}@{domain}', example: 'jane.doe@example.com' },
  { label: 'Initials concatenated + last', template: '{firstname_initial}{lastname}@{domain}', example: 'jdoe@example.com' },
  { label: 'Initials dot last', template: '{firstname_initial}.{lastname_initial}.{lastname}@{domain}', example: 'j.d.doe@example.com' },
  { label: 'Underscore separator', template: '{firstname}_{lastname}@{domain}', example: 'jane_doe@example.com' },
  { label: 'Last underscore first', template: '{lastname}_{firstname}@{domain}', example: 'doe_jane@example.com' },
  { label: 'Hyphen separator', template: '{firstname}-{lastname}@{domain}', example: 'jane-doe@example.com' },
  { label: 'Last dot first initial', template: '{lastname}.{firstname_initial}@{domain}', example: 'doe.j@example.com' },
  { label: 'Two + two letters', template: '{firstname_2}{lastname_2}@{domain}', example: 'jado@example.com' },
  { label: 'Three + three letters', template: '{firstname_3}.{lastname_3}@{domain}', example: 'jan.doe@example.com' },
  { label: 'Literal prefix emp_', template: 'emp_{firstname}.{lastname}@{domain}', example: 'emp_jane.doe@example.com' },
  { label: 'With middle segment', template: '{firstname}.{middle}.{lastname}@{domain}', example: 'jane.dev.doe@example.com', note: 'Set Middle field (e.g. dev)' },
  { label: 'With suffix after last', template: '{firstname}.{lastname}-{suffix}@{domain}', example: 'jane.doe-pilvi@example.com', note: 'Set Suffix field' },
  { label: 'Prefix + dotted names', template: '{prefix}.{firstname}.{lastname}@{domain}', example: 'co.jane.doe@example.com', note: 'Set Prefix field' },
];

export default function AdminUserIdPatterns() {
  const [patterns, setPatterns] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [template, setTemplate] = useState('{firstname}.{lastname}@{domain}');
  const [description, setDescription] = useState('');
  const [defaultDomainId, setDefaultDomainId] = useState('');
  const [prefixString, setPrefixString] = useState('');
  const [suffixString, setSuffixString] = useState('');
  const [middleString, setMiddleString] = useState('');
  const [formError, setFormError] = useState('');
  const [templateNorm, setTemplateNorm] = useState('');
  const [templateErr, setTemplateErr] = useState('');

  useEffect(() => {
    try {
      setTemplateNorm(sanitizePatternTemplate(template));
      setTemplateErr('');
    } catch (e) {
      setTemplateNorm('');
      setTemplateErr(e?.message || 'Invalid template');
    }
  }, [template]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, d] = await Promise.all([
        api.get('/admin/user-id-patterns'),
        api.get('/admin/company-domains'),
      ]);
      setPatterns(p.data?.data || []);
      const rawDom = d.data?.data;
      const domList = Array.isArray(rawDom) ? rawDom : rawDom?.domains ?? [];
      setDomains(domList.filter((x) => x.is_active));
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load.');
      setPatterns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setEditingId(null);
    setName('');
    setTemplate('{firstname}.{lastname}@{domain}');
    setDescription('');
    setDefaultDomainId('');
    setPrefixString('');
    setSuffixString('');
    setMiddleString('');
    setFormError('');
  };

  const openAdd = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setName(r.name || '');
    setTemplate(r.pattern_template || '');
    setDescription(r.description || '');
    setDefaultDomainId(r.default_domain_id || r.defaultDomain?.id || '');
    setPrefixString(r.prefix_string || r.prefixString || '');
    setSuffixString(r.suffix_string || r.suffixString || '');
    setMiddleString(r.middle_string || r.middleString || '');
    setFormError('');
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    resetForm();
  };

  const submit = async () => {
    setFormError('');
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        pattern_template: template.trim(),
        description: description.trim() || undefined,
        default_domain_id: defaultDomainId || undefined,
        prefix_string: prefixString.trim() || undefined,
        suffix_string: suffixString.trim() || undefined,
        middle_string: middleString.trim() || undefined,
      };
      if (editingId) {
        await api.patch(`/admin/user-id-patterns/${editingId}`, payload);
      } else {
        await api.post('/admin/user-id-patterns', payload);
      }
      setOpen(false);
      resetForm();
      load();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this pattern?')) return;
    try {
      await api.delete(`/admin/user-id-patterns/${id}`);
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Delete failed.');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        User ID patterns
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Login emails are built from the HR contact&apos;s first and last name (employee or contractor contacts only)
        plus the domain you choose when creating the user. Put fixed text or punctuation directly in the template, or
        use {'{prefix}'}, {'{suffix}'}, {'{middle}'} with the optional fields below. Optional default domain is a hint;
        the real domain is selected on each employee.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        {PLACEHOLDER_HELP}
      </Alert>
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Ready-made templates (20) — Use copies into the form</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Jane Doe, domain example.com. Set Prefix / Middle / Suffix fields when the template uses them. Patterns are validated on save.
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Example</TableCell>
                  <TableCell align="right"> </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PRESET_TEMPLATES.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {row.template}
                      </Typography>
                      {row.note && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {row.note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {row.example}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setTemplate(row.template);
                          setOpen(true);
                        }}
                      >
                        Use
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <Alert severity="success" variant="outlined" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Short examples (Prefix = Pilvi; Middle = dev; Suffix = pilvi; Jane / Doe; pilvisystems.com)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Use {'{middle}'} only between first and last name. Use {'{suffix}'} after {'{lastname}'} (end of the local part
          before @). Set a default domain on the pattern to skip picking the domain when creating users.
        </Typography>
        <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          <div>{`{prefix}-{firstname}.{lastname}@{domain}`} → pilvi-jane.doe@pilvisystems.com</div>
          <div>{`{prefix}.{firstname}.{lastname}@{domain}`} → pilvi.jane.doe@pilvisystems.com</div>
          <div>{`{firstname}.{middle}.{lastname}@{domain}`} → jane.dev.doe@pilvisystems.com</div>
          <div>{`{firstname}.{lastname}-{suffix}@{domain}`} → jane.doe-pilvi@pilvisystems.com</div>
          <div>{`{firstname}.{middle}.{lastname}-{suffix}@{domain}`} → jane.dev.doe-pilvi@pilvisystems.com</div>
        </Typography>
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openAdd}>
          Add pattern
        </Button>
      </Stack>
      <Card>
        <CardContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>P / S / M</TableCell>
                    <TableCell>Default domain</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patterns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No patterns yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    patterns.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.name}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {r.pattern_template}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {[r.prefix_string, r.suffix_string, r.middle_string].filter(Boolean).join(' · ') || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.defaultDomain?.domain || '—'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEdit(r)} aria-label="edit">
                            <EditOutlinedIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => remove(r.id)} aria-label="delete">
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit user ID pattern' : 'Add user ID pattern'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Pattern name" fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} />
            <TextField
              label="Pattern template"
              fullWidth
              multiline
              minRows={2}
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              error={Boolean(templateErr)}
              helperText={
                templateErr ||
                (templateNorm
                  ? `Will be saved as: ${templateNorm} (quotes and spaces removed). Must include @ and domain placeholders.`
                  : 'Must produce a full email including @.')
              }
            />
            <TextField
              label="Description (optional)"
              fullWidth
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <TextField
              select
              label="Optional default domain"
              fullWidth
              size="small"
              value={defaultDomainId}
              onChange={(e) => setDefaultDomainId(e.target.value)}
              helperText="If set, New User / employee login flows pre-fill this domain when this pattern is chosen (you can still override)."
            >
              <MenuItem value="">None</MenuItem>
              {domains.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.company_name} — {d.domain}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Prefix value for {prefix} (optional)"
              fullWidth
              size="small"
              value={prefixString}
              onChange={(e) => setPrefixString(e.target.value)}
              helperText="Letters and digits in the value; surrounding quotes are removed if pasted. Type Pilvi, not quoted Pilvi."
            />
            <TextField
              label="Suffix value for {suffix} (optional)"
              fullWidth
              size="small"
              value={suffixString}
              onChange={(e) => setSuffixString(e.target.value)}
              helperText="Same rules as prefix. A mistaken dev-in-braces token in the template is normalized to the suffix placeholder on save — enter that value here (e.g. dev)."
            />
            <TextField
              label="Middle value for {middle} (optional)"
              fullWidth
              size="small"
              value={middleString}
              onChange={(e) => setMiddleString(e.target.value)}
              helperText="Same rules as prefix (e.g. dev, us, east)."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : editingId ? 'Save changes' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
